(ns practitest.frontend.xbot.events
  (:require
    [goog.object :as gobj]
    [clojure.string :as string]
    [re-frame.core :as re-frame]
    [day8.re-frame.http-fx]
    [ajax.core :as ajax]
    [practitest.frontend.xbot.db :as db]
    [practitest.frontend.common.websocket :as ws]
    [practitest.frontend.common.logger :as log]))

;; ============================================================================
;; interceptors

(def common-interceptors
  [re-frame/trim-v])

;; ===========================================================================
;; constants

(def ws-reconnect-grace-period 1)

; seconds
(def ^:const request-timeout 60000)

(def check-agent-interval (* 5 60 1000))

;; ============================================================================
;; utils

(defn reconstruct-agents [agents]
  (->> agents
       (map
        (fn
          [[agent-id
            {:keys [xbot-id client-id platform platform-version last-connected enabled]}]]
          [(name agent-id)
           {:client-id client-id
            :id        xbot-id
            :machine   {:os (str platform " (" platform-version ")")}
            :now       last-connected
            :online?   false
            :enabled?  (= enabled "true")}]))
       (into {})))

;; TODO: refactor to new router
(defn refresh-data-request [options data-type page from-history?]
  (case data-type
    :history  {:method          :get
               :uri             (:xbot-history-url options)
               :response-format (ajax/json-response-format {:keywords? true})
               :on-success      [::history-refreshed page from-history?]
               :on-failure      [::api-error :refresh-history]}
    :running  {:method          :get
               :uri             (:xbot-running-url options)
               :response-format (ajax/json-response-format {:keywords? true})
               :on-success      [::running-refreshed]
               :on-failure      [::api-error :refresh-running]}
    :queue    {:method          :get
               :uri             (:xbot-queue-url options)
               :response-format (ajax/json-response-format {:keywords? true})
               :on-success      [::queue-refreshed]
               :on-failure      [::api-error :refresh-queue]}
    :schedule {:method          :get
               :uri             (:xbot-schedule-url options)
               :response-format (ajax/json-response-format {:keywords? true})
               :on-success      [::schedule-queue-refreshed]
               :on-failure      [::api-error :refresh-schedule-queue]}))

(defn older? [agent n unit]
  (.isAfter (js/moment (.subtract (js/moment) n unit)) (js/moment (:now agent))))

;; ============================================================================
;; event handlers

(re-frame/reg-event-fx
 ::initialize-db
 (fn [{db :db} [_ options]]
   (log/debug ::initialize-db options)
   (.addEventListener js/window "popstate"
                      (fn [e]
                        (log/info "popstate" e)
                        (re-frame/dispatch
                         [::restore-state
                          (js->clj (.-state e) :keywordize-keys true)])))
   {:db
    (cond-> (merge db
                   db/default-db
                   {:options options})
            (= "admin" (:view options))
            (assoc :agents (reconstruct-agents (:known-agents options)))
            (= "admin" (:view options))
            (assoc :page 1))
    :fx [[:dispatch [::get-access-token]]
         (when (= "admin" (:view options))
           [:dispatch [::check-agent-last-connected]])
         (when (contains? #{"run" "queue-management"} (:view options))
           [:dispatch-n
            [[::refresh-queue]
             [::refresh-running]
             [::refresh-schedule-queue]
             [::refresh-history 1 true]]])]}))

(re-frame/reg-event-fx
 ::get-access-token
 (fn [{:keys [db]} _]
   {:http-xhrio {:method          :get
                 :uri             (get-in db [:options :access-token-url])
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [::got-access-token]
                 :on-failure      [::api-error :get-access-token]}}))

(re-frame/reg-event-fx
 ::refresh-queue
 (log/debug ::refresh-queue)
 (fn [{:keys [db]} [_ from-history?]]
   {:db         (update db :remote #(set (conj % :refresh-queue)))
    :http-xhrio (refresh-data-request (:options db) :queue nil from-history?)}))

(re-frame/reg-event-db
 ::queue-refreshed
 (fn [db [_ data]]
   (log/debug ::queue-refreshed data)
   (-> db
       (assoc :queue data)
       (update :remote #(set (disj % :refresh-queue))))))

(re-frame/reg-event-fx
 ::refresh-history
 (log/debug ::refresh-history)
 (fn [{:keys [db]} [_ page from-history?]]
   {:db         (update db :remote #(set (conj % :refresh-history)))
    :http-xhrio (refresh-data-request (:options db) :history (or page 1) from-history?)}))

(re-frame/reg-event-db
 ::history-refreshed
 (fn [db [_ page from-history? data]]
   (log/debug ::history-refreshed page from-history? data)
   (when-not from-history?
     ;; if this event wasn't the result of user going back or forward in history
     ;; we need to push new state to the history
     (let [state {:current-qm-tab :history :page page}]
       (.pushState js/window.history (clj->js state) "")))
   (-> db
       (assoc :history data)
       (update :remote #(set (disj % :refresh-history))))))

(re-frame/reg-event-fx
 ::refresh-running
 (log/debug ::refresh-running)
 (fn [{:keys [db]} [_ from-history?]]
   {:db         (update db :remote #(set (conj % :refresh-running)))
    :http-xhrio (refresh-data-request (:options db) :running nil from-history?)}))

(re-frame/reg-event-db
 ::running-refreshed
 (fn [db [_ data]]
   (log/debug ::running-refreshed data)
   (-> db
       (assoc :running data)
       (update :remote #(set (disj % :refresh-running))))))

(re-frame/reg-event-fx
 ::refresh-schedule-queue
 (log/debug ::refresh-schedule-queue)
 (fn [{:keys [db]} [_ from-history?]]
   {:db         (update db :remote #(set (conj % :refresh-schedule-queue)))
    :http-xhrio (refresh-data-request (:options db) :schedule nil from-history?)}))

(re-frame/reg-event-db
 ::schedule-queue-refreshed
 (fn [db [_ data]]
   (log/debug ::schedule-queue-refreshed data)
   (-> db
       (assoc :schedule-queue data)
       (update :remote #(set (disj % :refresh-schedule-queue))))))

(re-frame/reg-event-fx
 ::delete-queue-item
 (fn [{:keys [db]} [_ item]]
   (log/debug ::delete-queue-item item)
   {:db         (update db :remote #(set (conj % :delete-queue-item)))
    :http-xhrio {:method          :post
                 :uri             (:url item)
                 :params          (select-keys item [:job-client-id :job-project-id :job-run-id])
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [::queue-item-deleted]
                 :on-failure      [::api-error :delete-queue-item]}}))

(re-frame/reg-event-fx
 ::queue-item-deleted
 (fn [{:keys [db]} [_ data]]
   (log/debug ::queue-item-deleted data)
   {:db       (update db :remote #(set (disj % :delete-queue-item)))
    :dispatch [::refresh-queue]}))

(re-frame/reg-event-fx
 ::kill-running-item
 (fn [{:keys [db]} [_ item]]
   (log/debug ::kill-running-item item)
   {:db         (update db :remote #(set (conj % :kill-running-item)))
    :http-xhrio {:method          :post
                 :uri             (if (some? (:url item))
                                    (:url item)
                                    (get-in db [:options :kill-run-from-agent-url]))
                 :params          (select-keys item [:job-project-id :job-agent-id])
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [::running-item-killed item]
                 :on-failure      [::api-error :kill-running-item]}}))

(re-frame/reg-event-fx
 ::running-item-killed
 (fn [{:keys [db]} [_ data]]
   (log/debug ::running-item-killed data)
   {:db (-> db
            (assoc-in [:agents (:job-agent-id data) :busy?] false)
            (update :remote #(set (disj % :kill-running-item))))}
   (when (some? (:url data))
     {:dispatch [::refresh-queue]})
   (when (:also-disable-agent data)
     {:dispatch [::toggle-enable-agent {:agent-id (:job-agent-id data) :enabled? false}]})))

(re-frame/reg-event-fx
 ::delete-schedule-item
 (fn [{:keys [db]} [_ item]]
   (log/debug ::delete-schedule-item item)
   {:db         (update db :remote #(set (conj % :delete-schedule-item)))
    :http-xhrio {:method          :post
                 :uri             (:url item)
                 :params          (select-keys item [:job-project-id :job-suite-id])
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [::schedule-item-deleted]
                 :on-failure      [::api-error :delete-schedule-item]}}))

(re-frame/reg-event-fx
 ::schedule-item-deleted
 (fn [{:keys [db]} [_ data]]
   (log/debug ::schedule-item-deleted data)
   {:db       (update db :remote #(set (disj % :delete-schedule-item)))
    :dispatch [::refresh-schedule-queue]}))

(re-frame/reg-event-fx
 ::skip-next-run-item
 (fn [{:keys [db]} [_ item]]
   (log/debug ::skip-next-run-item item)
   {:db         (update db :remote #(set (conj % :skip-next-run-item)))
    :http-xhrio {:method          :post
                 :uri             (:url item)
                 :params          (select-keys item [:job-project-id :job-suite-id :skip])
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [::next-run-item-skiped]
                 :on-failure      [::api-error :skip-next-run-item]}}))

(re-frame/reg-event-fx
 ::next-run-item-skiped
 (fn [{:keys [db]} [_ data]]
   (log/debug ::schedule-item-deleted data)
   {:db       (update db :remote #(set (disj % :skip-next-run-item)))
    :dispatch [::refresh-schedule-queue]}))

(re-frame/reg-event-fx
 ::run-now-item
 (fn [{:keys [db]} [_ item]]
   (log/debug ::run-now-item item)
   {:db         (update db :remote #(set (conj % :run-now-item)))
    :http-xhrio {:method          :post
                 :uri             (:url item)
                 :params          (select-keys item [:id])
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [::item-ran]
                 :on-failure      [::api-error :run-now-item]}}))

(re-frame/reg-event-fx
 ::item-ran
 (fn [{:keys [db]} [_ data]]
   (log/debug ::item-ran data)
   {:db       (update db :remote #(set (disj % :run-now-item)))
    :dispatch [::refresh-history]}))

(re-frame/reg-event-fx
 ::got-access-token
 (fn [{:keys [db]} [_ data]]
   (log/debug ::got-access-token data)
   ;; if the token is empty, we need to redirect to login
   (when (nil? (:token data))
     (let [location   (gobj/get js/document "location")
           logout-url (get-in db [:options :logout-url])]
       (log/debug "Redirecting to" logout-url)
       (.replace location logout-url)))
   {:db       (assoc db :credentials data)
    :dispatch [(if (= :connected (get-in db [:xbot-backend :state]))
                 ::close-xbot-backend
                 ::initialize-xbot-backend)]}))

(re-frame/reg-event-fx
 ::close-xbot-backend
 (fn [{:keys [db]} _]
   (ws/destroy-ws (:xbot-backend db))
   {}))

(re-frame/reg-event-fx
 ::initialize-xbot-backend
 (fn [{:keys [db]} _]
   (log/debug ::initialize-xbot-backend)
   (let [ws-url (str (get-in db [:options :xbot-backend-url])
                     "?access-token="
                     (get-in db [:credentials :token])
                     "&account-id="
                     (get-in db [:options :account-id])
                     (when-let [run-id (get-in db [:options :run-id])]
                       (str "&run-id=" run-id)))
         ws     (ws/make-ws
                 {:url        ws-url
                  :format     :json
                  :on-open    #(re-frame/dispatch [::ws-open-xbot-backend])
                  :on-message #(re-frame/dispatch [::ws-message-xbot-backend %])
                  :on-error   #(re-frame/dispatch [::ws-error-xbot-backend %])
                  :on-close   #(re-frame/dispatch [::ws-close-xbot-backend %1 %2])})]
     {:db (assoc db :xbot-backend ws)})))

(re-frame/reg-event-db
 ::ws-open-xbot-backend
 (fn [db _]
   (log/info ::ws-open-xbot-backend)
   (assoc-in db [:xbot-backend :state] :connected)))

(re-frame/reg-event-db
 ::ws-message-xbot-backend
 (fn [db [_ message]]
   (log/debug ::ws-message-xbot-backend message)
   (case (:type message)
     "telemetry-history"
     (assoc db :current-telemetry (:telemetry message))

     "telemetry"
     (cond-> db
       (and (:current-run-id message)
            (= (str (get-in db [:options :run-id])) (:current-run-id message)))
       (assoc-in [:current-run :telemetry]
                 {:id        (:xbot-id message)
                  :xbot-id   (:xbot-id message)
                  :agent-id  (:agent-id message)
                  :client-id (:client-id message)
                  :load      (:load message)
                  :online?   true
                  :enabled?  (get-in db [:agents (:agent-id message) :enabled?])
                  :busy?     (get-in db [:current-run :telemetry :busy?])
                  :stdout    (get-in db [:current-run :telemetry :stdout])
                  :stderr    (get-in db [:current-run :telemetry :stderr])
                  :cpu       (:cpu_percentage message)
                  :memory    {:total (get-in message [:memory :total])
                              :used  (get-in message [:memory :usedPercent])}
                  :uptime    (:uptime message)
                  :now       (:now message)
                  :machine   {:uptime (get-in message [:host :uptime])
                              :os     (str (get-in message [:host :os])
                                           " ("
                                           (get-in message [:host :platformVersion])
                                           ")")
                              :cpus   (map :cores (:cpu message))}})

       (and (:current-run-id message)
            (= (str (get-in db [:options :run-id])) (:current-run-id message)))
       (update :current-telemetry conj message)

       :always (assoc-in [:agents (:agent-id message)]
                         {:id        (:xbot-id message)
                          :client-id (:client-id message)
                          :online?   true
                          :enabled?  (:enabled message)
                          :busy?     (get-in db [:agents (:agent-id message) :busy?])
                          :stdout    (get-in db [:agents (:agent-id message) :stdout])
                          :stderr    (get-in db [:agents (:agent-id message) :stderr])
                          :load      (:load message)
                          :cpu       (:cpu_percentage message)
                          :memory    {:total (get-in message [:memory :total])
                                      :used  (get-in message [:memory :usedPercent])}
                          :uptime    (:uptime message)
                          :now       (:now message)
                          :machine   {:uptime (get-in message [:host :uptime])
                                      :os     (str (get-in message [:host :os])
                                                   " ("
                                                   (get-in message [:host :platformVersion])
                                                   ")")
                                      :cpus   (map :cores (:cpu message))}}))

     ("process:stdout" "process:stderr")
     (-> db
         (update-in
          [:agents
           (:agent-id message)
           (keyword
            (-> (:type message)
                (string/split #":")
                last))]
          conj (select-keys message [:timestamp :text]))
         (update-in
          [:current-run
           :telemetry
           (keyword
            (-> (:type message)
                (string/split #":")
                last))]
          conj (select-keys message [:timestamp :text]))
         (assoc-in [:current-run :telemetry :busy?] true))

     "disconnect"
     (-> db
         (assoc-in [:agents (:agent-id message) :online?] false)
         (assoc-in [:agents (:agent-id message) :enabled?] true))

     "process:started"
     (-> db
         (assoc-in [:agents (:agent-id message) :busy?] true)
         (assoc-in [:current-run :telemetry :busy?] true))

     ("process:success" "process:error" "process:timeout" "process:failure")
     (do
       (when-let [on-complete (get-in db [:options :on-complete])]
         (on-complete
          (case (:type message)
            "process:success"                   "PASSED"
            ("process:error" "process:timeout") "FAILED"
            "process:failure"                   "ERROR")))
       (-> db
           (assoc-in [:agents (:agent-id message) :busy?] false)
           (assoc-in [:current-run :telemetry :busy?] false)
           (update-in [:agents (:agent-id message)] dissoc :stdout :stderr)))
     db)))

(re-frame/reg-event-db
 ::ws-error-xbot-backend
 (fn [db [_ error]]
   ;; TODO: proper errors handling
   (log/error ::ws-error-xbot-backend error)
   db))

(re-frame/reg-event-fx
 ::ws-close-xbot-backend
 (fn [{:keys [db]} [_ code reason]]
   (log/info ::ws-close-xbot-backend code reason)
   ;; we want to always reconnect with something like 5sec interval
   {:db             (assoc db :xbot-backend {:state :disconnected})
    :dispatch-later {:ms (* 1000 ws-reconnect-grace-period) :dispatch [::get-access-token]}}))

(re-frame/reg-event-db
 ::api-error
 (fn [db [_ api error]]
   (log/error ::api-error api error)
   (-> db
       (update :remote #(set (disj % api))))))

(re-frame/reg-event-db
 ::bad-http-result
 [common-interceptors]
 (fn [db [error]]
   (log/info ::bad-http-result error)
   (-> db
       ; (dissoc :loading? :report-definition-updating? :instances-refreshing)
       (assoc :error (:response error)))))

(re-frame/reg-event-db
 ::set-qm-tab
 [common-interceptors]
 (fn [db [tab]]
   ;; we want the user to be able to use browser back/forward
   ;; for that, we need to push the state consisting of currently selected tab and page number
   ;; this handler is called when user changes queue tab, so page number is always 1
   (let [state {:current-qm-tab tab :page 1}]
     (.pushState js/window.history (clj->js state) ""))
   (assoc db :current-qm-tab tab)))

(re-frame/reg-event-fx
 ::restore-state
 (fn [{:keys [db]} [_ state]]
   (let [state (or state {:current-qm-tab "queue" :page 1})
         tab   (keyword (:current-qm-tab state))]
     {:db (assoc db :current-qm-tab tab)
      :fx (case tab
            :queue    [[:dispatch-n
                        [[::refresh-queue true]
                         [::refresh-running true]]]]
            :schedule [[:dispatch [::refresh-schedule-queue true]]]
            :history  [[:dispatch [::refresh-history (:page state) true]]])})))

(re-frame/reg-event-fx
 ::set-agent-client-id
 common-interceptors
 (fn [{:keys [db]} [item]]
   (log/debug ::set-agent-client-id item)
   {:db         (update db :remote #(set (conj % :set-agent-client-id)))
    :http-xhrio {:method          :post
                 :uri             (get-in db [:options :update-agent-client-id-url])
                 :params          (select-keys item [:agent-id :client-id])
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [::agent-client-id-set item]
                 :on-failure      [::bad-http-result ::set-agent-client-id]}}))

(re-frame/reg-event-db
 ::agent-client-id-set
 common-interceptors
 (fn [db [data]]
   (log/debug ::agent-client-id-set data)
   (-> db
       (assoc-in [:agents (:agent-id data) :client-id] (:client-id data))
       (update :remote #(set (disj % :set-agent-client-id))))))

(re-frame/reg-event-fx
 ::toggle-enable-agent
 common-interceptors
 (fn [{:keys [db]} [item]]
   (log/debug ::toggle-enable-agent item)
   {:db         (update db :remote #(set (conj % :toggle-enable-agent)))
    :http-xhrio {:method          :post
                 :uri             (get-in db [:options :toggle-enable-agent-url])
                 :params          (select-keys item [:agent-id :enabled?])
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [::enable-agent-toggled item]
                 :on-failure      [::bad-http-result ::toggle-enable-agent]}}))

(re-frame/reg-event-db
 ::enable-agent-toggled
 common-interceptors
 (fn [db [data]]
   (log/debug ::enable-agent-toggled data)
   (-> db
       (assoc-in [:agents (:agent-id data) :enabled?] (:enabled? data))
       (update :remote #(set (disj % :toggle-enable-agent))))))

(re-frame/reg-event-fx
 ::remove-agent
 common-interceptors
 (fn [{:keys [db]} [item]]
   (log/debug ::remove-agent item)
   {:db         (update db :remote #(set (conj % :remove-agent)))
    :http-xhrio {:method          :post
                 :uri             (get-in db [:options :remove-agent-url])
                 :params          (select-keys item [:agent-id])
                 :format          (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success      [::agent-removed item]
                 :on-failure      [::bad-http-result ::remove-agent]}}))

(re-frame/reg-event-db
 ::agent-removed
 common-interceptors
 (fn [db [data]]
   (log/debug ::agent-removed data)
   (-> db
       (update-in [:agents] dissoc (:agent-id data))
       (update :remote #(set (disj % :remove-agent))))))

(re-frame/reg-event-fx
 ::check-agent-last-connected
 common-interceptors
 (fn [{:keys [db]}]
   (log/debug ::check-agent-last-connected)
   {:db (update db :agents
                (fn [agents]
                  (->> agents
                       (map (fn [[agent-id agent]]
                              [agent-id
                               (if (older? agent 15 "m")
                                 (assoc agent :remove-btn-trigger (rand-int 1000))
                                 (dissoc agent :remove-btn-trigger))]))
                       (into {}))))
    :dispatch-later {:ms check-agent-interval :dispatch [::check-agent-last-connected]}}))
